import React from 'react';

/**
 * Label row that hosts a `.field-label` (or `.form-label`) plus optional
 * inline adornments (StatusBadge, InfoBadge) and an optional trailing action
 * (micro icon-button).
 *
 * Solves the alignment bug where wrapping `.field-label` / `.form-label` in
 * a `flex items-center gap-2 mb-2` div compounded margins (the label class
 * already has its own `mb-*`), pushing the field-display below by ~8px and
 * breaking horizontal alignment with sibling cells in a CSS grid.
 *
 * Geometry contract (non-negotiable):
 *   - Row height is FIXED (`h-*`, not `min-h-*`) so badges/actions cannot
 *     stretch one cell off the shared grid baseline.
 *   - Spacing below the label is owned by the row height — inner `<label>`
 *     has `mb-0`; the row never adds its own `mb-*`.
 *   - Trailing `action` is laid out inside the row (`ml-auto`). It must be
 *     a micro control (`icon-button-* icon-button-sm`, 32px). Grids that
 *     place an action on ANY cell MUST use `size="comfortable"` on EVERY
 *     FieldLabelRow in that grid — including cells with no action — so the
 *     label track is uniform.
 *
 * Heights:
 *   - field default      → h-6  (24px) ≈ `.field-label` text + mb-1
 *   - field comfortable  → h-8  (32px) fits `icon-button-sm`
 *   - form default       → h-7  (28px) ≈ `.form-label` text + mb-2
 *   - form comfortable   → h-8  (32px) fits `icon-button-sm`
 *
 * Keep in sync with `.field-label` / `.form-label` in `frontend/src/index.css`.
 *
 * Props:
 *   label           - string (required)
 *   children        - ReactNode (optional) — badges / adornments after label
 *   action          - ReactNode (optional) — trailing micro control (ml-auto)
 *   theme           - 'field' | 'form' (default 'field')
 *   size            - 'default' | 'comfortable' (default 'default')
 *   icon            - ReactNode (optional) — leading icon inside the label
 *   htmlFor         - string (optional)
 *   className       - string (optional) — extra classes on the wrapper
 *   labelClassName  - string (optional) — extra classes on the <label>
 */

const ROW_THEMES = {
  field: {
    default: 'h-6',
    comfortable: 'h-8',
    label: 'field-label mb-0',
  },
  form: {
    default: 'h-7',
    comfortable: 'h-8',
    label: 'form-label mb-0',
  },
};

const FieldLabelRow = ({
  label,
  children,
  action = null,
  theme = 'field',
  size = 'default',
  icon,
  htmlFor,
  className = '',
  labelClassName = '',
}) => {
  const t = ROW_THEMES[theme] || ROW_THEMES.field;
  const rowHeight = t[size] || t.default;
  const labelClasses = [
    t.label,
    icon ? 'flex items-center gap-2' : '',
    labelClassName,
  ].filter(Boolean).join(' ');

  return (
    <div className={`flex items-center gap-2 ${rowHeight} ${className}`.trim()}>
      <label htmlFor={htmlFor} className={`${labelClasses} min-w-0 truncate`.trim()}>
        {icon}
        {label}
      </label>
      {children && (
        <div className="flex items-center gap-1.5 min-w-0 overflow-hidden">
          {children}
        </div>
      )}
      {action && (
        <div className="ml-auto flex items-center shrink-0">
          {action}
        </div>
      )}
    </div>
  );
};

export default FieldLabelRow;
